/* ==============================================================
   Monograph quick-add - 2026-08-28

   The homepage rail and the collection grid render one <form
   action="/cart/add" method="post"> per product with a submit
   button per size, each carrying name="id". They are NOT wrapped
   in <product-form>, so Dawn's product-form.js never saw them and
   clicking a size did a full page POST that landed the customer on
   /cart. This turns those into an AJAX add that opens the cart
   drawer, which is where the free-shipping bar lives.

   Written as a delegated listener on document rather than per
   section, so any future section using the same plain-form pattern
   is covered with no extra wiring.

   Deliberately does NOT touch <product-form> forms. Dawn already
   AJAXes those and intercepting them would double-fire.

   NOTE 2026-08-31: a SECOND block now lives at the bottom of this
   file, the Maison Jaxob one-per-scent guard. See its own header.
   ============================================================== */
(function () {
  'use strict';

  var BUSY = 'mono-quick-add--busy';

  function cartElement() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function sectionIds(cart) {
    try {
      return cart.getSectionsToRender().map(function (section) {
        return section.id;
      });
    } catch (err) {
      return ['cart-drawer', 'cart-icon-bubble'];
    }
  }

  document.addEventListener(
    'submit',
    function (evt) {
      var form = evt.target;
      if (!form || form.tagName !== 'FORM') return;

      var action = (form.getAttribute('action') || '').split('?')[0];
      if (action.indexOf('/cart/add') === -1) return;
      if (form.closest('product-form')) return;

      var cart = cartElement();
      if (!cart || typeof cart.renderContents !== 'function') return;

      var submitter = evt.submitter;
      var id = submitter && submitter.name === 'id' ? submitter.value : null;
      if (!id) {
        var hidden = form.querySelector('[name="id"]');
        id = hidden ? hidden.value : null;
      }
      if (!id) return;

      evt.preventDefault();
      if (form.classList.contains(BUSY)) return;
      form.classList.add(BUSY);
      if (submitter) submitter.setAttribute('aria-busy', 'true');

      var body = new FormData(form);
      body.set('id', id);
      if (!body.get('quantity')) body.set('quantity', '1');
      body.append('sections', sectionIds(cart).join(','));
      body.append('sections_url', window.location.pathname);

      var url = (window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js';

      fetch(url, { method: 'POST', headers: { Accept: 'application/javascript' }, body: body })
        .then(function (response) {
          return response.json();
        })
        .then(function (parsed) {
          if (parsed.status) throw new Error(parsed.description || parsed.message || 'add failed');
          if (submitter && typeof cart.setActiveElement === 'function') cart.setActiveElement(submitter);
          /* Dawn's CartDrawer.renderContents() tries to clear `is-empty`, but it
             looks for the class on .drawer__inner while Liquid actually sets it on
             the <cart-drawer> host. Adding from an EMPTY cart therefore left it in
             place, and `.is-empty .cart__contents{display:none}` hid the line items,
             `cart-drawer.is-empty .drawer__header{display:none}` hid the title, and
             `cart-drawer.is-empty .drawer__inner{padding:0}` collapsed the padding so
             the remaining text clipped at both edges. A blank drawer on a first-time
             visitor's first add, which is the worst possible audience for it.
             product-form.js clears the class itself, which is why the product page
             never showed this and the rail always did. Reproduced and fixed
             2026-09-14. */
          cart.classList.remove('is-empty');
          cart.renderContents(parsed);
          form.classList.remove(BUSY);
          if (submitter) submitter.removeAttribute('aria-busy');
        })
        .catch(function () {
          /* Never swallow the add. Fall back to the plain POST, which still
             works, it just costs a page load. form.submit() skips submit
             handlers so this cannot recurse. */
          form.classList.remove(BUSY);
          if (submitter) submitter.removeAttribute('aria-busy');
          if (submitter && submitter.name === 'id') {
            var carry = document.createElement('input');
            carry.type = 'hidden';
            carry.name = 'id';
            carry.value = id;
            form.appendChild(carry);
          }
          form.submit();
        });
    },
    true
  );
})();

/* ==============================================================
   Monograph - Maison Jaxob, one per scent per order - 2026-08-31

   Phil's rule: a customer may take at most ONE unit of any given
   Maison Jaxob fragrance per order, counted across sizes. So one
   Devil's Breath, in one size. They may still take all eight
   scents in the same order - that is the point, the limit exists
   to spread the range rather than to shrink the basket.

   The Discovery Set is deliberately EXEMPT. Two sets go to two
   people, which serves the same goal.

   The same rule is stated in prose on the Maison Jaxob collection
   page. If the rule changes, change both.

   WHY THIS LIVES IN THIS FILE: it needs window.routes and
   cart-drawer.js, and this asset is already tagged at the very end
   of <body> in layout/theme.liquid, after both. Adding a second
   <script> tag there would mean rewriting a 24KB layout file that a
   Dawn update has already clobbered once. Two IIFEs, one tag.

   ENFORCEMENT IS CLIENT-SIDE AND THEREFORE SOFT. It stops the
   quantity selector, the quick-add rail and the cart drawer, which
   is every route a real customer takes. A hand-built /cart/add POST
   or a cart permalink can still get past it. A hard limit needs a
   Shopify cart-validation function (custom app) or a paid order-limit
   app. Judged not worth it at 24 orders/month.
   ============================================================== */
(function () {
  'use strict';

  var VENDOR = 'Maison Jaxob';
  var HANDLE_PREFIX = 'maison-jaxob-';
  var EXEMPT = ['maison-jaxob-discovery-set'];
  var NOTE = 'One per fragrance, so the range reaches more people.';
  var busy = false;

  function isExempt(handle) {
    return EXEMPT.indexOf(handle) !== -1;
  }

  function cartElement() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function sectionIds(cart) {
    try {
      return cart.getSectionsToRender().map(function (section) {
        return section.id;
      });
    } catch (err) {
      return ['cart-drawer', 'cart-icon-bubble'];
    }
  }

  /* ---- product page: cap the selector at one ---- */
  function capProductPage() {
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (!match) return;
    var handle = match[1];
    if (handle.indexOf(HANDLE_PREFIX) !== 0 || isExempt(handle)) return;

    /* Scoped to MainContent on purpose. The cart drawer is present in the
       DOM on every page and its line-item quantity inputs carry the same
       class; capping those would limit unrelated products. */
    var scope = document.getElementById('MainContent');
    if (!scope) return;

    scope.querySelectorAll('.quantity__input').forEach(function (input) {
      input.max = '1';
      if (parseInt(input.value, 10) > 1) input.value = '1';
    });
    scope.querySelectorAll('.quantity__button[name="plus"]').forEach(function (button) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    });

    if (document.getElementById('mono-jaxob-note')) return;
    var note = document.createElement('p');
    note.id = 'mono-jaxob-note';
    note.textContent = NOTE;
    /* Full-strength ink, not the usual muted helper grey. Phil asked for it
       black so it actually gets read before the size is chosen. */
    note.style.cssText =
      'margin:1.2rem 0 .4rem;font-family:\'IBM Plex Mono\',ui-monospace,Menlo,monospace;' +
      'font-size:1.2rem;line-height:1.5;letter-spacing:.06em;color:#17140f;';

    /* Sits directly beneath the size picker, above quantity and the buy
       buttons, so it is read while the size is still being chosen. */
    var anchor =
      scope.querySelector('variant-selects') ||
      scope.querySelector('variant-radios') ||
      scope.querySelector('.product-form__input');
    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(note, anchor.nextSibling);
      return;
    }
    var host = scope.querySelector('.product-form__buttons') || scope.querySelector('product-form');
    if (host) host.appendChild(note);
  }

  /* ---- cart: clamp each Jaxob product to a single unit ---- */
  function clampCart() {
    if (busy) return;
    busy = true;

    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var seen = {};
        var fixes = [];

        (cart.items || []).forEach(function (item) {
          if (item.vendor !== VENDOR) return;
          if (isExempt(item.handle)) return;
          var pid = String(item.product_id);
          if (seen[pid]) {
            /* a second size of a scent already in the cart */
            fixes.push({ id: item.key, quantity: 0 });
          } else {
            seen[pid] = true;
            if (item.quantity > 1) fixes.push({ id: item.key, quantity: 1 });
          }
        });

        if (!fixes.length) {
          busy = false;
          return null;
        }
        return applyFixes(fixes);
      })
      .catch(function () {
        busy = false;
      });
  }

  function applyFixes(fixes) {
    var cart = cartElement();
    var url = ((window.routes && window.routes.cart_change_url) || '/cart/change') + '.js';
    var chain = Promise.resolve(null);

    fixes.forEach(function (fix, index) {
      chain = chain.then(function () {
        var body = { id: fix.id, quantity: fix.quantity };
        /* Ask for re-rendered sections on the LAST change only, so the cart
           UI is repainted once rather than once per fix. */
        if (index === fixes.length - 1 && cart) {
          body.sections = sectionIds(cart).join(',');
          body.sections_url = window.location.pathname;
        }
        return fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        }).then(function (response) {
          return response.json();
        });
      });
    });

    return chain
      .then(function (parsed) {
        busy = false;
        if (parsed && parsed.sections && cart && typeof cart.renderContents === 'function') {
          cart.renderContents(parsed);
        } else if (window.location.pathname.indexOf('/cart') === 0) {
          window.location.reload();
          return;
        }
        flash(NOTE + ' Your cart has been adjusted.');
      })
      .catch(function () {
        busy = false;
      });
  }

  function flash(message) {
    var existing = document.getElementById('mono-jaxob-flash');
    if (existing) existing.remove();
    var el = document.createElement('div');
    el.id = 'mono-jaxob-flash';
    el.setAttribute('role', 'status');
    el.textContent = message;
    el.style.cssText =
      'position:fixed;left:50%;bottom:2.4rem;transform:translateX(-50%);z-index:100;' +
      'max-width:34rem;padding:1.2rem 1.8rem;background:#17140f;color:#f3ead8;' +
      'font-size:1.3rem;line-height:1.5;letter-spacing:.03em;text-align:center;' +
      'box-shadow:0 2px 18px rgba(0,0,0,.28);';
    document.body.appendChild(el);
    setTimeout(function () {
      if (el.parentNode) el.remove();
    }, 6000);
  }

  /* DOMContentLoaded, not immediate: every Dawn script on this page is
     deferred, so subscribe() and PUB_SUB_EVENTS only exist by then. */
  document.addEventListener('DOMContentLoaded', function () {
    capProductPage();
    clampCart();
    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      /* No loop risk: a clamped cart produces no fixes on the next pass,
         and `busy` guards the overlap. */
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        setTimeout(clampCart, 250);
      });
    }
  });
})();

/* ==============================================================
   Monograph - minus at one removes the line - 2026-09-14

   Phil: "if you press minus on a 1 item line, it deletes the item
   as opposed to having to press the trash can."

   Dawn renders cart quantity inputs with min="0" but data-min="1".
   CartItems.validateQuantity() compares the typed value against
   data-min, so stepping 1 -> 0 fails the check: the box visibly
   shows 0, a validation message is set, and the cart is never
   updated. The row just sits there looking stuck. Verified live
   before this fix: value went to "0", item_count stayed at 2.

   So intercept the click at exactly quantity 1 and hand off to the
   row's own <cart-remove-button>, which is Dawn's supported removal
   path (it calls cartItems.updateQuantity(index, 0) and re-renders
   the sections properly). Nothing here reimplements cart mutation.

   CAPTURE PHASE on purpose: quantity-input binds its own bubble
   listener to these buttons, so a capture listener plus
   stopPropagation is what keeps stepDown() from running first.

   SCOPED to cart-drawer-items and cart-items. The product page has
   the same button markup and minus at 1 must stay a no-op there,
   since there is no line to remove.
   ============================================================== */
(function () {
  'use strict';

  document.addEventListener(
    'click',
    function (evt) {
      var target = evt.target;
      if (!target || typeof target.closest !== 'function') return;

      var minus = target.closest('.quantity__button[name="minus"]');
      if (!minus) return;
      if (!minus.closest('cart-drawer-items, cart-items')) return;

      var wrap = minus.closest('quantity-input');
      var input = wrap && wrap.querySelector('.quantity__input');
      if (!input) return;
      if (parseInt(input.value, 10) !== 1) return;

      var row = minus.closest('.cart-item');
      var remove = row && row.querySelector('cart-remove-button');
      if (!remove) return;

      evt.preventDefault();
      evt.stopPropagation();
      remove.click();
    },
    true
  );
})();

/* ==============================================================
   Monograph - Growave rewards launcher, dressed for the site - 2026-10-06

   Growave draws its "Rewards" launcher inside an open shadow root
   on <div class="gw-rd-popup-widget-placeholder">, so neither
   monograph.css nor the theme editor's custom CSS can reach it.
   Out of the box it is a rounded navy pill in Arial with a trophy.

   Colours are set in Growave itself (Branding > Rewards: ink
   primary, gold secondary, launcher "Text only"). This block does
   the part Growave has no setting for: square corners, no shadow,
   and the site's Courier capitals, by appending one <style> into
   that shadow root once it exists. Nothing else inside the widget
   is touched, so the popup keeps Growave's own layout.

   Same file for the same reason as the Jaxob block above: this
   asset is already tagged at the end of <body>, and theme.liquid
   stays untouched.
   ============================================================== */
(function () {
  'use strict';

  var HOST = '.gw-rd-popup-widget-placeholder';
  var MARK = 'data-monograph-launcher';
  var CSS =
    '.gw-launcher{font-family:"IBM Plex Mono","Courier New",Courier,monospace!important;' +
    'font-size:11px!important;letter-spacing:.2em!important;text-transform:uppercase!important;' +
    'font-weight:400!important;border-radius:0!important;box-shadow:none!important;' +
    'padding:15px 22px!important;min-height:0!important;height:auto!important;}' +
    '.gw-launcher__text,.gw-launcher .gw-text{font-family:inherit!important;font-size:inherit!important;' +
    'letter-spacing:inherit!important;font-weight:inherit!important;line-height:1!important;}';

  function dress(host) {
    var root = host.shadowRoot;
    if (!root || root.querySelector('style[' + MARK + ']')) return;
    var style = document.createElement('style');
    style.setAttribute(MARK, '');
    style.textContent = CSS;
    root.appendChild(style);
  }

  function scan() {
    var hosts = document.querySelectorAll(HOST);
    for (var i = 0; i < hosts.length; i++) dress(hosts[i]);
  }

  scan();
  if (typeof MutationObserver === 'function') {
    new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
